import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionSummary } from '../../../types/subscription';
import {
  selectOverviewSubSort,
  setOverviewSubSort,
  selectOverviewSubView,
  setOverviewSubView,
} from '../../../store/slices/ui';

export const SORT_OPTIONS = [
  'Total Value',
  'Total Stable Profit',
  'Total Asset Profit',
  'Current Stable Profit',
  'Current Asset Profit',
  'Non-Algo Diff',
] as const;
export type SortOption = (typeof SORT_OPTIONS)[number];
export type ViewMode = 'grid' | 'list';

/**
 * Return the value used to *sort* subscriptions for a given sort option.
 *
 * - "Total Value" → absolute total balance (always positive).
 * - All others    → the relative (percentage) profit, so subscriptions
 *                   with the largest %-move come first regardless of size.
 */
export function getSortValue(
  sub: SubscriptionSummary,
  sort: SortOption,
): number {
  switch (sort) {
    case 'Total Stable Profit':
      return sub.profits?.total.stable.relative ?? 0;
    case 'Total Asset Profit':
      return sub.profits?.total.asset.relative ?? 0;
    case 'Current Stable Profit':
      return sub.latestTransaction?.orderType === 'BUY'
        ? sub.profits?.current.stable?.relative ?? 0
        : 0;
    case 'Current Asset Profit':
      return sub.latestTransaction?.orderType === 'SELL'
        ? sub.profits?.current.asset?.relative ?? 0
        : 0;
    case 'Non-Algo Diff':
      return sub.profits?.noAlgorithmicProgressionProfit.relative ?? 0;
    default:
      return sub.totalBalanceInStable;
  }
}

interface FilterContextType {
  query: string;
  setQuery: (q: string) => void;
  sort: SortOption;
  setSort: (s: SortOption) => void;
  view: ViewMode;
  setView: (v: ViewMode) => void;
  filteredSubs: SubscriptionSummary[];
  sortedSubs: SubscriptionSummary[];
}

const noop = () => undefined;

const FilterContext = createContext<FilterContextType>({
  query: '',
  setQuery: noop,
  sort: 'Total Value',
  setSort: noop,
  view: 'grid',
  setView: noop,
  filteredSubs: [],
  sortedSubs: [],
});

export const FilterProvider: React.FC<{
  children: React.ReactNode;
  subscriptions: SubscriptionSummary[];
}> = ({ children, subscriptions }) => {
  const [query, setQuery] = useState('');
  const dispatch = useDispatch();
  const sort = useSelector(selectOverviewSubSort) as SortOption;
  const setSort = useCallback(
    (s: SortOption) => {
      dispatch(setOverviewSubSort(s));
    },
    [dispatch],
  );
  const view = useSelector(selectOverviewSubView) as ViewMode;
  const setView = useCallback(
    (v: ViewMode) => {
      dispatch(setOverviewSubView(v));
    },
    [dispatch],
  );

  const filteredSubs = useMemo(() => {
    if (!query) return subscriptions;
    const q = query.toLowerCase();
    return subscriptions.filter(
      (s) =>
        s.baseAsset.toLowerCase().includes(q) ||
        s.quoteAsset.toLowerCase().includes(q) ||
        s.exchange.toLowerCase().includes(q),
    );
  }, [query, subscriptions]);

  const sortedSubs = useMemo(() => {
    if (sort === 'Total Value') {
      return [...filteredSubs].sort(
        (a, b) => getSortValue(b, sort) - getSortValue(a, sort),
      );
    }
    // Sort by absolute percentage so "biggest mover" wins regardless of sign.
    return [...filteredSubs].sort(
      (a, b) =>
        Math.abs(getSortValue(b, sort)) - Math.abs(getSortValue(a, sort)),
    );
  }, [filteredSubs, sort]);

  return (
    <FilterContext.Provider
      value={{
        query,
        setQuery,
        sort,
        setSort,
        view,
        setView,
        filteredSubs,
        sortedSubs,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = () => useContext(FilterContext);
